/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 메뉴아이템을 타입에 따라 정의한다.
 *
 * @file /src/models/table/filters/Filter.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import { Custom as MenuItem } from '@components/menu/elements/items/Custom';
import { Field } from '@components/form/field/Field';
import { Container } from '@components/form/field/container/Container';
import { Button } from '@namespaces/components/Button';
import { Display } from '@components/form/field/display/Display';
import { Language } from '@utils/Language';

export interface Properties {
    /**
     * 필터명
     */
    name?: string;

    /**
     * 필터를 적용할 dataIndex
     */
    dataIndex?: string;
}

export abstract class Filter {
    /**
     * 필터타입
     */
    abstract type: string;

    /**
     * 필터명
     */
    name: string | null;

    /**
     * 메뉴아이템
     */
    menuItem: MenuItem = null;

    /**
     * 필터필드
     */
    field: Field = null;

    /**
     * 데이터인덱스
     */
    dataIndex: string | null = null;

    /**
     * 필터적용버튼
     */
    button: Button = null;

    /**
     * 컬럼객체
     */
    handler: (dataIndex: string | null, filter: { value: any; operator: string } | null) => Promise<void> = null;

    /**
     * 필터를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        this.name = this.properties.name ?? null;
        this.dataIndex = this.properties.dataIndex ?? null;
    }

    /**
     * 필터핸들러를 등록한다.
     *
     * @param handler - 필터핸들러
     */
    setHandler(
        handler: (dataIndex: string | null, filter: { value: any; operator: string } | null) => Promise<void>
    ): void {
        this.handler = handler;
    }

    /**
     * 메뉴아이템을 가져온다.
     */
    getMenu(): MenuItem {
        if (this.menuItem === null) {
            this.menuItem = new MenuItem({
                name: this.name,
                type: 'custom',
                icon: 'mi mi-filter',
                custom: new Container({
                    direction: 'column',
                    minWidth: this.getField().width ?? 200,
                    items: [
                        this.getField(),
                        new Container({
                            direction: 'row',
                            items: [
                                new Display({
                                    flex: 1,
                                }),
                                new Button({
                                    text: Language.get('models.table.filter.reset'),
                                    handler: async (button) => {
                                        button.setLoading(true);
                                        const menu = button.getParent().getParent().getParent();
                                        if (this.handler !== null) {
                                            await this.handler(this.dataIndex, null);
                                        }

                                        button.setLoading(false);
                                        menu.hide();
                                    },
                                }),
                                this.getButton(),
                            ],
                        }),
                    ],
                }),
            });
        }

        return this.menuItem;
    }

    /**
     * 필터적용버튼을 가져온다.
     *
     * @returns 필터적용버튼
     */
    getButton(): Button {
        if (this.button === null) {
            this.button = new Button({
                text: Language.get('models.table.filter.confirm'),
                buttonClass: 'confirm',
                handler: async (button) => {
                    button.setLoading(true);
                    const menu = button.getParent().getParent().getParent();

                    if (this.handler !== null) {
                        const filter = await this.getFilter();
                        await this.handler(this.dataIndex, filter);
                    }

                    button.setLoading(false);
                    menu.hide();
                },
            });
        }

        return this.button;
    }

    /**
     * 필터의 최대높이를 설정한다.
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: number): void {}

    /**
     * 필터필드를 가져온다.
     *
     * @returns 필드
     */
    abstract getField(): Field;

    /**
     * 필터객체를 가져온다.
     *
     * @returns 필터객체
     */
    abstract getFilter(): Promise<{ value: any; operator: string } | null>;

    /**
     * 현재 필터설정을 필터필드에 적용한다.
     *
     * @param filter - 필터객체
     */
    abstract setFilter(filter: { value: any; operator: string } | null): Promise<void>;
}
